/**
 * El único lugar de la aplicación que habla con la API.
 *
 * Corre en los dos lados. En el servidor —dentro de un loader— la API está en la misma máquina
 * y se la llama por su dirección interna; en el navegador se la llama con una ruta relativa,
 * porque nginx la publica bajo el mismo dominio. Esa es toda la diferencia, y vive acá adentro
 * para que ninguna pantalla tenga que saberla.
 *
 * La sesión viaja en una cookie HttpOnly que pone la API. Nada de esto la lee ni la puede leer,
 * que es el punto: no hay token en el navegador que un script inyectado pueda llevarse.
 */

/**
 * Dónde está la API cuando la llama el servidor.
 *
 * El `typeof process` no es un adorno. Este módulo también se carga en el navegador, donde
 * `process` no existe: al compilar, Vite reemplaza `process.env` por un objeto vacío y no pasa
 * nada, pero en desarrollo no lo reemplaza y la línea tira `process is not defined` antes de que
 * se dibuje nada. El panel entero quedaba en blanco, y sólo en local.
 */
const INTERNA =
  (typeof process === 'undefined' ? undefined : process.env.API_URL) ?? 'http://127.0.0.1:5100';

/** `true` cuando este código corre en Node y no en el navegador. */
const enElServidor = typeof document === 'undefined';

/** Lo que la API contesta cuando algo sale mal: siempre `{ error: "..." }`. */
interface ErrorBody {
  error?: string;
}

/**
 * Un pedido que la API rechazó, con el estado y el mensaje que mandó.
 *
 * El mensaje viene en castellano desde el servidor porque es lo que lee una persona en
 * pantalla; acá no se traduce ni se reescribe. Inventar un texto propio cuando la API ya
 * explicó qué pasó ("Ya existe un usuario con ese nombre") es cambiarle el error a alguien por
 * uno peor.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** La sesión venció o nunca existió. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Está conectado, pero no le alcanza el rol. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * El error que se muestra cuando la API rechaza algo.
 *
 * Si la API explicó qué pasó, ese mensaje es el que se muestra: viene en castellano y escrito
 * para una persona. El texto de reserva es para cuando se cayó algo antes de llegar a la
 * aplicación —nginx, el contenedor, la red— y no hay nada que contar.
 */
function errorDe(
  respuesta: Response,
  cuerpo: ErrorBody | null,
  reserva = 'No se pudo completar la operación. Probá de nuevo.',
): ApiError {
  return new ApiError(respuesta.status, cuerpo?.error ?? reserva);
}

export type Query = Record<string, string | number | boolean | null | undefined>;

interface Options {
  query?: Query;

  /** Se serializa a JSON. Para subir archivos, pasar un FormData en `form`. */
  json?: unknown;

  /** Para subir archivos. No se le pone Content-Type: lo arma el navegador con el boundary. */
  form?: FormData;

  /**
   * El pedido original del visitante.
   *
   * Sólo tiene sentido en el servidor y es obligatorio cuando la respuesta depende de quién
   * pregunta: la cookie de sesión viaja ahí adentro, y sin reenviarla la API contesta como si
   * fuera un visitante anónimo. Es lo que decide, por ejemplo, si el catálogo trae precios.
   */
  request?: Request;

  signal?: AbortSignal;
}

function conParametros(path: string, query?: Query): string {
  if (!query) return path;

  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(query)) {
    if (valor !== null && valor !== undefined && valor !== '') {
      params.set(clave, String(valor));
    }
  }

  const texto = params.toString();
  return texto ? `${path}?${texto}` : path;
}

/** La URL absoluta en el servidor, relativa en el navegador. */
export function url(path: string, query?: Query): string {
  const ruta = conParametros(path, query);
  return enElServidor ? `${INTERNA}${ruta}` : ruta;
}

async function pedir<T>(method: string, path: string, options: Options = {}): Promise<T> {
  const headers: Record<string, string> = {};

  if (options.json !== undefined) headers['Content-Type'] = 'application/json';

  if (options.request) {
    const cookie = options.request.headers.get('cookie');
    if (cookie) headers.cookie = cookie;
  }

  const respuesta = await fetch(url(path, options.query), {
    method,
    headers,
    // En el navegador es el mismo origen y la cookie iría igual; esto lo deja explícito.
    credentials: 'include',
    signal: options.signal,
    body: options.form ?? (options.json !== undefined ? JSON.stringify(options.json) : undefined),
  });

  if (respuesta.status === 204) {
    return undefined as T;
  }

  const esJson = respuesta.headers.get('content-type')?.includes('application/json');
  const cuerpo = esJson ? await respuesta.json().catch(() => null) : null;

  if (!respuesta.ok) throw errorDe(respuesta, cuerpo as ErrorBody | null);

  return cuerpo as T;
}

export const get = <T>(path: string, options?: Options) => pedir<T>('GET', path, options);

/**
 * Lo mismo, pero un fallo devuelve el valor de reserva en vez de romper la pantalla.
 *
 * Para lo que decora y no para lo que la página es: si se cae el listado de banners, la portada
 * tiene que salir igual sin el carrusel. Si se cae el catálogo, no —ahí el error se muestra.
 */
export async function getOr<T>(path: string, reserva: T, options?: Options): Promise<T> {
  try {
    return await get<T>(path, options);
  } catch {
    return reserva;
  }
}

export const api = {
  get,
  getOr,
  post: <T>(path: string, options?: Options) => pedir<T>('POST', path, options),
  put: <T>(path: string, options?: Options) => pedir<T>('PUT', path, options),
  patch: <T>(path: string, options?: Options) => pedir<T>('PATCH', path, options),
  delete: <T>(path: string, options?: Options) => pedir<T>('DELETE', path, options),

  /**
   * Baja un archivo y se lo ofrece al navegador.
   *
   * No alcanza con un `<a href>` apuntando a la API: el nombre del archivo viaja en
   * Content-Disposition y hay endpoints que contestan 400 o 404 con un JSON explicando por qué
   * —el PDF de una marca sin piezas cargadas, por ejemplo—. Bajándolo así, ese error llega como
   * un ApiError y la pantalla lo puede mostrar, en vez de dejar al navegador guardando un
   * archivo que en realidad es un mensaje de error.
   */
  async download(path: string, options?: Omit<Options, 'json' | 'form'>): Promise<void> {
    const respuesta = await fetch(url(path, options?.query), { credentials: 'include' });

    if (!respuesta.ok) {
      const cuerpo = (await respuesta.json().catch(() => null)) as ErrorBody | null;
      throw errorDe(respuesta, cuerpo, 'No se pudo descargar el archivo.');
    }

    const disposicion = respuesta.headers.get('content-disposition') ?? '';
    const nombre = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposicion)?.[1];

    const enlace = document.createElement('a');
    const objeto = URL.createObjectURL(await respuesta.blob());

    enlace.href = objeto;
    enlace.download = nombre ? decodeURIComponent(nombre) : 'descarga';
    document.body.append(enlace);
    enlace.click();
    enlace.remove();

    // El objeto queda en memoria hasta que se lo suelta, y son archivos de varios megabytes.
    URL.revokeObjectURL(objeto);
  },
};
